import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Card, CardContent } from "@/components/Card";
import { Coffee, Gift, ShoppingBag, Ticket, Umbrella } from "lucide-react";
import React, { useState } from "react";
import { cn } from "@/lib/utils";
import AppLayout from '@/layouts/AppLayout';
import { usePage, router } from '@inertiajs/react';
import { useConfirm } from '@/hooks/useConfirm';

const DEFAULT_REWARD = {
  id: 'default',
  name: "Tarjeta de Regalo",
  cost: 100,
  category: "Tarjeta de Regalo",
  description: "Tarjeta de regalo genérica",
  image_url: null,
  stock: null,
  is_active: true
};

function Redeem({ rewards = [], user_balance = 0 }) {
  const [balance, setBalance] = useState(user_balance);
  const { confirm, ConfirmDialog } = useConfirm();

  const handleRedeem = async (reward) => {
    if (balance < reward.cost) {
      await confirm({
        title: 'Puntos insuficientes',
        message: 'No tienes suficientes puntos.',
        confirmText: 'Entendido',
        cancelText: '',
        variant: 'warning'
      });
      return;
    }

    const confirmed = await confirm({
      title: 'Confirmar canje',
      message: `¿Estás seguro de canjear "${reward.name}" por ${reward.cost} puntos?`,
      confirmText: 'Canjear',
      variant: 'warning'
    });

    if (!confirmed) return;

    router.post(`/rewards/${reward.id}/redeem`, {}, {
      onSuccess: async () => {
        setBalance(prev => prev - reward.cost);
        await confirm({
          title: '¡Canje exitoso!',
          message: 'Recibirás los detalles en tu correo.',
          confirmText: 'Entendido',
          cancelText: '',
          variant: 'success'
        });
      },
      onError: async (errors) => {
        console.error("Redemption error:", errors);
        await confirm({
          title: 'Error al canjear',
          message: 'Hubo un error al procesar el canje.',
          confirmText: 'Entendido',
          cancelText: '',
          variant: 'danger'
        });
      }
    });
  };

  const handleDefaultReward = async () => {
    await confirm({
      title: 'Premios no configurados',
      message: 'Por favor contacta a administración para configurar los premios reales.',
      confirmText: 'Entendido',
      cancelText: '',
      variant: 'info'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-center bg-white p-6 rounded-lg shadow-sm border border-bambu-green-pastel">
        <div>
          <h1 className="text-2xl font-bold text-bambu-black">Catálogo de Premios</h1>
          <p className="text-gray-500">Canjea tus puntos acumulados por increíbles recompensas.</p>
        </div>
        <div className="mt-4 md:mt-0 text-right">
          <span className="block text-sm text-gray-500 uppercase tracking-wide">Tu Saldo</span>
          <span className="text-4xl font-bold text-bambu-green">{balance} pts</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {(rewards.length > 0 ? rewards : [DEFAULT_REWARD]).map(reward => {
          const canAfford = balance >= reward.cost;
          const outOfStock = reward.stock !== null && reward.stock <= 0;
          const isDefault = reward.id === 'default';

          return (
            <Card key={reward.id} className={cn("hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1", (outOfStock || isDefault) && "opacity-80")}>
              <CardContent className="p-6 flex flex-col items-center text-center h-full">
                <div className="w-full h-32 mb-4 bg-gray-50 rounded-lg flex items-center justify-center overflow-hidden">
                  {reward.image_url ? (
                    <img src={reward.image_url} alt={reward.name} className="w-full h-full object-cover" />
                  ) : (
                    <Gift className="w-12 h-12 text-bambu-green/30" />
                  )}
                </div>

                <h3 className="text-lg font-bold text-bambu-black mb-1 line-clamp-1">{reward.name}</h3>
                <Badge variant="secondary" className="mb-2">{reward.category || 'General'}</Badge>
                {outOfStock && <Badge variant="destructive" className="mb-2">Agotado</Badge>}
                <p className="text-xs text-gray-500 line-clamp-2 mb-4 h-8">{reward.description}</p>

                <div className="mt-auto w-full">
                  <p className="text-2xl font-bold text-bambu-green mb-4">{reward.cost} pts</p>
                  <Button
                    className="w-full"
                    variant={canAfford && !outOfStock && !isDefault ? "primary" : "secondary"}
                    disabled={!canAfford || outOfStock || isDefault}
                    onClick={() => isDefault ? handleDefaultReward() : handleRedeem(reward)}
                  >
                    {isDefault ? "No Configurado" : outOfStock ? "Agotado" : canAfford ? "Canjear" : "Insuficientes Puntos"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDialog />
    </div>
  );
}

Redeem.layout = page => <AppLayout children={page} />;
export default Redeem;
